import { _difference } from '@naturalcycles/js-lib'
import { languageContext } from '@src/context/language.context'
import { mixpanelService } from '@src/srv/mixpanel.service'
import classNames from 'classnames'
import type { ReactNode } from 'react'
import { useContext, useRef } from 'react'
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
  AccordionItemState,
} from 'react-accessible-accordion'
import styles from './FaqAccordion.module.scss'

export interface FaqAccordionProps {
  questions: {
    question: string
    answer: string
  }[]
  className?: string
  headingLevel?: number
  theme?: 'grey' | 'lightGrey' | 'white' | 'whiteWithRoundedCorners' | 'card'
  marginTop?: 'none' | 'regular'
  fullWidth?: boolean
}

export function FaqAccordion({
  questions,
  className = '',
  headingLevel = 2,
  theme = 'grey',
  marginTop = 'regular',
  fullWidth = false,
}: FaqAccordionProps): ReactNode {
  const openQuestions = useRef<string[]>([])
  const { locale } = useContext(languageContext)

  function handleChange(uuids: string[]): void {
    const questions = uuids.map(u => decodeURIComponent(u))
    const newQuestions = _difference(questions, openQuestions.current)

    newQuestions.forEach(question => {
      mixpanelService.trackClick('faq-question', {
        question,
        locale,
      })
    })
    openQuestions.current = questions
  }

  return (
    <Accordion
      className={classNames(className, styles[`FaqAccordion___${theme}Theme`], {
        [styles.FaqAccordion___noMarginTop]: marginTop === 'none',
        [styles.FaqAccordion___fullWidth]: fullWidth,
      })}
      onChange={handleChange}
      allowMultipleExpanded
      allowZeroExpanded
    >
      {questions.map(({ question, answer }) => (
        <AccordionItem
          key={question}
          uuid={encodeURIComponent(question)}
          className={styles.FaqAccordion__item}
        >
          <AccordionItemState>
            {({ expanded }) => (
              <>
                <AccordionItemHeading aria-level={headingLevel}>
                  <AccordionItemButton className={styles.FaqAccordion__button}>
                    <span className={styles.FaqAccordion__question}>{question}</span>
                    <span
                      className={classNames(styles.FaqAccordion__arrow, {
                        [styles.FaqAccordion__arrowExpanded]: expanded,
                      })}
                    />
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel>
                  <div
                    className={styles.FaqAccordion__answer}
                    dangerouslySetInnerHTML={{ __html: answer }}
                  />
                </AccordionItemPanel>
              </>
            )}
          </AccordionItemState>
        </AccordionItem>
      ))}
    </Accordion>
  )
}
